// The Curve — API helpers + guest identity.
// Guests get a device UUID in localStorage; it rides every request as a
// header. After magic-link auth, claimGuestDraws() merges it into the account.

const GUEST_KEY = 'ftCurveGuest';

export function getGuestToken() {
  try {
    let token = localStorage.getItem(GUEST_KEY);
    if (!token) {
      token =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `g-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(GUEST_KEY, token);
    }
    return token;
  } catch (e) {
    return null; // storage blocked — server will reject locks without identity
  }
}

export function clearGuestToken() {
  try {
    localStorage.removeItem(GUEST_KEY);
  } catch (e) {
    /* noop */
  }
}

const REQUEST_TIMEOUT_MS = 15000;

async function request(path, options = {}) {
  const guest = getGuestToken();
  // Phones/PWAs can resume from the background with a half-dead connection
  // that never errors — plain fetch() then hangs forever and the caller's
  // loading state never clears. Force a timeout so it always settles.
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  let res;
  try {
    res = await fetch(`/api/curve${path}`, {
      credentials: 'include',
      ...options,
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        ...(guest ? { 'x-ft-curve-guest': guest } : {}),
        ...(options.headers || {}),
      },
    });
  } catch (e) {
    throw e.name === 'AbortError' ? new Error('Request timed out') : e;
  } finally {
    clearTimeout(timer);
  }
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(body.error || `Request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return body;
}

export const fetchToday = () => request('/today');
export const lockDraw = (points) =>
  request('/draw', { method: 'POST', body: JSON.stringify({ points }) });
export const fetchTape = () => request('/tape');
export const fetchShared = (shareId) => request(`/r/${shareId}`);
export const claimGuestDraws = (guestToken) =>
  request('/claim', { method: 'POST', body: JSON.stringify({ guestToken }) });

// admin
export const fetchSchedule = () => request('/admin/schedule');
export const saveScheduleOrder = (orderedIds) =>
  request('/admin/schedule/reorder', { method: 'POST', body: JSON.stringify({ orderedIds }) });
export const searchSchedulePlayers = (q) =>
  request(`/admin/schedule/search?q=${encodeURIComponent(q)}`);
export const addSchedulePlayer = (playerId) =>
  request('/admin/schedule/add', { method: 'POST', body: JSON.stringify({ playerId }) });
